import { RouteReuseStrategy, DetachedRouteHandle, ActivatedRouteSnapshot } from '@angular/router';

interface StoredRoute {
  handle: DetachedRouteHandle;
  scrollY: number;
}

export class CustomRouteReuseStrategy implements RouteReuseStrategy {
  private storedRoutes = new Map<string, StoredRoute>();

  // 🔹 Lista de rotas que devem ser mantidas
  private reusableRoutes = [
    'home/(home:historical)',
    '(primary:complete-account)'
  ];

  // 🔹 Define quais rotas devem ser mantidas em cache
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    const path = this.getRoutePath(route);
    return this.reusableRoutes.includes(path);
  }

  // 🔹 Armazena a rota destacada e a posição do scroll
  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const path = this.getRoutePath(route);
    if (handle && this.reusableRoutes.includes(path)) {
      this.storedRoutes.set(path, {
        handle,
        scrollY: window.scrollY
      });
    }
  }

  // 🔹 Define se a rota pode ser reutilizada (reativada)
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const path = this.getRoutePath(route);
    return this.storedRoutes.has(path);
  }

  // 🔹 Recupera a instância armazenada do componente
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const path = this.getRoutePath(route);
    return this.storedRoutes.get(path)?.handle ?? null;
  }

  // 🔹 Evita recriar o componente quando é a mesma rota
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig;
  }

  // 🔹 Helper para identificar rotas (considerando outlet e pai)
  private getRoutePath(route: ActivatedRouteSnapshot): string {
    const namedOutlet = route.outlet ? `(${route.outlet}:${route.routeConfig?.path})` : route.routeConfig?.path;
    const parentPath = route.parent?.routeConfig?.path ? `${route.parent.routeConfig.path}/` : '';
    return parentPath + (namedOutlet ?? '');
  }

  // 🔹 Método público opcional — restaura o scroll automaticamente após voltar
  restoreScrollPosition(route: ActivatedRouteSnapshot): void {
    const path = this.getRoutePath(route);
    const stored = this.storedRoutes.get(path);
    if (stored) {
      setTimeout(() => window.scrollTo({ top: stored.scrollY, behavior: 'auto' }), 0);
    }
  }

  // 🔹 Limpa o cache manualmente (por exemplo, no logout)
  clearStoredRoutes(): void {
    this.storedRoutes.clear();
  }

  // 🔹 Remove apenas uma rota específica do cache
  clearRoute(path: string): void {
    this.storedRoutes.delete(path);
  }
}
